/* eslint-disable indent */
import { classNames } from 'primereact/utils';
import PropTypes from 'prop-types';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { generateDiffStrings } from 'utils/func/diffString';
import DiffColumn from '../DiffColumn';

export default function ExpenditureCompareTable({ expenditure1, expenditure2, diffObj }) {
  const { t } = useTranslation();

  const expenditureFromVNUHCMTotal1 = useMemo(() => {
    if (expenditure1?.proposedExpenditureSummaries === undefined) return 0;
    return expenditure1?.proposedExpenditureSummaries?.reduce((total, item) => {
      if (item.proposedExpenditureType?.subProposedExpenditureTypeId === null) {
        return total + (item?.expenditureFromVNUHCM ?? 0);
      }
      return total;
    }, 0);
  });
  const expenditureFromFundingTotal1 = useMemo(() => {
    if (expenditure1?.proposedExpenditureSummaries === undefined) return 0;
    return expenditure1?.proposedExpenditureSummaries?.reduce((total, item) => {
      if (item.proposedExpenditureType?.subProposedExpenditureTypeId === null) {
        return total + (item?.expenditureFromFunding ?? 0);
      }
      return total;
    }, 0);
  });

  const expenditureFromVNUHCMTotal2 = useMemo(() => {
    if (expenditure2?.proposedExpenditureSummaries === undefined) return 0;
    return expenditure2?.proposedExpenditureSummaries?.reduce((total, item) => {
      if (item.proposedExpenditureType?.subProposedExpenditureTypeId === null) {
        return total + (item?.expenditureFromVNUHCM ?? 0);
      }
      return total;
    }, 0);
  });
  const expenditureFromFundingTotal2 = useMemo(() => {
    if (expenditure2?.proposedExpenditureSummaries === undefined) return 0;
    return expenditure2?.proposedExpenditureSummaries?.reduce((total, item) => {
      if (item.proposedExpenditureType?.subProposedExpenditureTypeId === null) {
        return total + (item?.expenditureFromFunding ?? 0);
      }
      return total;
    }, 0);
  });

  return (
    <tr>
      <td className="py-2">
        <div
          className="p-datatable p-component p-datatable-responsive-scroll p-datatable-scrollable p-datatable-gridlines p-datatable-gridlines"
          data-scrollselectors=".p-datatable-wrapper"
        >
          <div className="p-datatable-wrapper">
            <table className="p-datatable-table">
              <thead className="p-datatable-thead">
                <tr>
                  <th style={{ minWidth: '12rem', textAlign: 'center' }}>
                    <span className="p-column-title">
                      {t('projectProposal.expenditure.expenditure.fields.term')}
                    </span>
                  </th>
                  <th style={{ minWidth: '6rem', textAlign: 'center' }}>
                    <span className="p-column-title">
                      {t('projectProposal.expenditure.expenditure.fields.from_vnu')}
                    </span>
                  </th>
                  <th style={{ minWidth: '6rem', textAlign: 'center' }}>
                    <span className="p-column-title">
                      {t('projectProposal.expenditure.expenditure.fields.from_funding')}
                    </span>
                  </th>
                  <th style={{ minWidth: '6rem', textAlign: 'center' }}>
                    <span className="p-column-title">
                      {t('projectProposal.expenditure.expenditure.fields.total')}
                    </span>
                  </th>
                </tr>
              </thead>
              <tbody className="p-datatable-tbody">
                {expenditure1?.proposedExpenditureSummaries?.map((_, index) => (
                  <tr key={expenditure1?.proposedExpenditureSummaries?.[index]?.id || index}>
                    <td
                      className={classNames({
                        'font-bold':
                          expenditure1?.proposedExpenditureSummaries?.[index]
                            ?.proposedExpenditureType?.subProposedExpenditureTypeId === null,
                      })}
                    >
                      {
                        generateDiffStrings(
                          t('lang') === 'vi'
                            ? expenditure1?.proposedExpenditureSummaries?.[index]
                                ?.proposedExpenditureType?.name
                            : expenditure1?.proposedExpenditureSummaries?.[index]
                                ?.proposedExpenditureType?.englishName,
                          t('lang') === 'vi'
                            ? expenditure2?.proposedExpenditureSummaries?.[index]
                                ?.proposedExpenditureType?.name
                            : expenditure2?.proposedExpenditureSummaries?.[index]
                                ?.proposedExpenditureType?.englishName
                        )?.left
                      }
                    </td>
                    <td className="text-center">
                      {
                        generateDiffStrings(
                          (
                            expenditure1?.proposedExpenditureSummaries?.[index]
                              ?.expenditureFromVNUHCM || ''
                          )?.toLocaleString('vi'),
                          (
                            expenditure2?.proposedExpenditureSummaries?.[index]
                              ?.expenditureFromVNUHCM || ''
                          )?.toLocaleString('vi')
                        )?.left
                      }
                    </td>
                    <td className="text-center">
                      {
                        generateDiffStrings(
                          (
                            expenditure1?.proposedExpenditureSummaries?.[index]
                              ?.expenditureFromFunding || ''
                          )?.toLocaleString('vi'),
                          (
                            expenditure2?.proposedExpenditureSummaries?.[index]
                              ?.expenditureFromFunding || ''
                          )?.toLocaleString('vi')
                        )?.left
                      }
                    </td>
                    <td className="text-center">
                      {
                        generateDiffStrings(
                          (
                            (expenditure1?.proposedExpenditureSummaries?.[index]
                              ?.expenditureFromVNUHCM ?? 0) +
                              (expenditure1?.proposedExpenditureSummaries?.[index]
                                ?.expenditureFromFunding ?? 0) || ''
                          )?.toLocaleString('vi'),
                          (
                            (expenditure2?.proposedExpenditureSummaries?.[index]
                              ?.expenditureFromVNUHCM ?? 0) +
                              (expenditure2?.proposedExpenditureSummaries?.[index]
                                ?.expenditureFromFunding ?? 0) || ''
                          )?.toLocaleString('vi')
                        )?.left
                      }
                    </td>
                  </tr>
                ))}

                <tr>
                  <td className="font-bold">
                    {t('projectProposal.expenditure.expenditure.total')}
                  </td>
                  <td className="text-center">
                    {
                      generateDiffStrings(
                        (expenditureFromVNUHCMTotal1 || '')?.toLocaleString('vi'),
                        (expenditureFromVNUHCMTotal2 || '')?.toLocaleString('vi')
                      )?.left
                    }
                  </td>
                  <td className="text-center">
                    {
                      generateDiffStrings(
                        (expenditureFromFundingTotal1 || '')?.toLocaleString('vi'),
                        (expenditureFromFundingTotal2 || '')?.toLocaleString('vi')
                      )?.left
                    }
                  </td>
                  <td className="text-center">
                    {
                      generateDiffStrings(
                        (
                          expenditureFromVNUHCMTotal1 + expenditureFromFundingTotal1 || ''
                        )?.toLocaleString('vi'),
                        (
                          expenditureFromVNUHCMTotal2 + expenditureFromFundingTotal2 || ''
                        )?.toLocaleString('vi')
                      )?.left
                    }
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </td>
      <td className="py-2">
        <div
          className="p-datatable p-component p-datatable-responsive-scroll p-datatable-scrollable p-datatable-gridlines p-datatable-gridlines"
          data-scrollselectors=".p-datatable-wrapper"
        >
          <div className="p-datatable-wrapper">
            <table className="p-datatable-table">
              <thead className="p-datatable-thead">
                <tr>
                  <th style={{ minWidth: '12rem', textAlign: 'center' }}>
                    <span className="p-column-title">
                      {t('projectProposal.expenditure.expenditure.fields.term')}
                    </span>
                  </th>
                  <th style={{ minWidth: '6rem', textAlign: 'center' }}>
                    <span className="p-column-title">
                      {t('projectProposal.expenditure.expenditure.fields.from_vnu')}
                    </span>
                  </th>
                  <th style={{ minWidth: '6rem', textAlign: 'center' }}>
                    <span className="p-column-title">
                      {t('projectProposal.expenditure.expenditure.fields.from_funding')}
                    </span>
                  </th>
                  <th style={{ minWidth: '6rem', textAlign: 'center' }}>
                    <span className="p-column-title">
                      {t('projectProposal.expenditure.expenditure.fields.total')}
                    </span>
                  </th>
                </tr>
              </thead>
              <tbody className="p-datatable-tbody">
                {expenditure2?.proposedExpenditureSummaries?.map((_, index) => (
                  <tr key={expenditure2?.proposedExpenditureSummaries?.[index]?.id || index}>
                    <td
                      className={classNames({
                        'font-bold':
                          expenditure2?.proposedExpenditureSummaries?.[index]
                            ?.proposedExpenditureType?.subProposedExpenditureTypeId === null,
                      })}
                    >
                      {
                        generateDiffStrings(
                          t('lang') === 'vi'
                            ? expenditure1?.proposedExpenditureSummaries?.[index]
                                ?.proposedExpenditureType?.name
                            : expenditure1?.proposedExpenditureSummaries?.[index]
                                ?.proposedExpenditureType?.englishName,
                          t('lang') === 'vi'
                            ? expenditure2?.proposedExpenditureSummaries?.[index]
                                ?.proposedExpenditureType?.name
                            : expenditure2?.proposedExpenditureSummaries?.[index]
                                ?.proposedExpenditureType?.englishName
                        )?.right
                      }
                    </td>
                    <td className="text-center">
                      {
                        generateDiffStrings(
                          (
                            expenditure1?.proposedExpenditureSummaries?.[index]
                              ?.expenditureFromVNUHCM || ''
                          )?.toLocaleString('vi'),
                          (
                            expenditure2?.proposedExpenditureSummaries?.[index]
                              ?.expenditureFromVNUHCM || ''
                          )?.toLocaleString('vi')
                        )?.right
                      }
                    </td>
                    <td className="text-center">
                      {
                        generateDiffStrings(
                          (
                            expenditure1?.proposedExpenditureSummaries?.[index]
                              ?.expenditureFromFunding || ''
                          )?.toLocaleString('vi'),
                          (
                            expenditure2?.proposedExpenditureSummaries?.[index]
                              ?.expenditureFromFunding || ''
                          )?.toLocaleString('vi')
                        )?.right
                      }
                    </td>
                    <td className="text-center">
                      {
                        generateDiffStrings(
                          (
                            (expenditure1?.proposedExpenditureSummaries?.[index]
                              ?.expenditureFromVNUHCM ?? 0) +
                              (expenditure1?.proposedExpenditureSummaries?.[index]
                                ?.expenditureFromFunding ?? 0) || ''
                          )?.toLocaleString('vi'),
                          (
                            (expenditure2?.proposedExpenditureSummaries?.[index]
                              ?.expenditureFromVNUHCM ?? 0) +
                              (expenditure2?.proposedExpenditureSummaries?.[index]
                                ?.expenditureFromFunding ?? 0) || ''
                          )?.toLocaleString('vi')
                        )?.right
                      }
                    </td>
                  </tr>
                ))}

                <tr>
                  <td className="font-bold">
                    {t('projectProposal.expenditure.expenditure.total')}
                  </td>
                  <td className="text-center">
                    {
                      generateDiffStrings(
                        (expenditureFromVNUHCMTotal1 || '')?.toLocaleString('vi'),
                        (expenditureFromVNUHCMTotal2 || '')?.toLocaleString('vi')
                      )?.right
                    }
                  </td>
                  <td className="text-center">
                    {
                      generateDiffStrings(
                        (expenditureFromFundingTotal1 || '')?.toLocaleString('vi'),
                        (expenditureFromFundingTotal2 || '')?.toLocaleString('vi')
                      )?.right
                    }
                  </td>
                  <td className="text-center">
                    {
                      generateDiffStrings(
                        (
                          expenditureFromVNUHCMTotal1 + expenditureFromFundingTotal1 || ''
                        )?.toLocaleString('vi'),
                        (
                          expenditureFromVNUHCMTotal2 + expenditureFromFundingTotal2 || ''
                        )?.toLocaleString('vi')
                      )?.right
                    }
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </td>
      <DiffColumn
        isDifferent={Object.values(diffObj?.proposedExpenditureSummaries ?? {})?.some(
          (e) => e?.expenditureFromFunding === true || e?.expenditureFromVNUHCM === true
        )}
      />
    </tr>
  );
}

ExpenditureCompareTable.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  expenditure1: PropTypes.object.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  expenditure2: PropTypes.object.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  diffObj: PropTypes.array.isRequired,
};
